import React, { useContext, useEffect } from 'react';
import CardContainer from 'components/redesign/CardContainer';
import KpiContainer from 'components/redesign/KpiContainer';
import GridContainer from 'components/redesign/GridContainer';
import MixLineOverlapBarChart from 'components/MixLineOverlapBarChart';
import { useOverview } from 'hooks/overview-hook';
import { FilterContext } from 'components/FilterBar/FilterProvider';
import { constants, screenSizes } from '../../../../../constants/Constants';
import KpiDefinitionData from 'service/KpiDefinitionData';
import useCustomMediaQuery from 'utils/useCustomMediaQuery';
import {
  createAppPerformanceData,
  createDataList,
} from '../helper/appPerformance';
import { B2BOverviewStyles as Styled } from '../../style';
import { useMetricsCriteria } from 'hooks/useMetricsCriteria';
import IncidentCountView from './IncidentCountView';
import { incidentMonitoringScorecrad } from '../helper/incidentMonitoringScorecrad';
import { useApplicationPerformance } from 'hooks/application-performance-hook';

interface AppPerformanceHOCProps {
  slug?: string;
  title: string;
  isOverview?: boolean;
}

const AppPerformanceHOC: React.FC<AppPerformanceHOCProps> = ({
  slug,
  title,
  isOverview = false,
}) => {
  const defaultAssignmentGroup = { value: 9999, label: 'All' };
  const defaultFrequency = { value: 182, label: 'Monthly' };
  const {
    ytdCheckbox,
    selectedProduct,
    categories,
    selectedApplicationNames,
    selectedPlant,
    selectedProgram,
  } = useContext(FilterContext);
  const isMobile = useCustomMediaQuery(screenSizes.mobile);
  const isTablet = useCustomMediaQuery(screenSizes.tablet);
  const { operationalMetricCriteria } = useMetricsCriteria();

  const {
    applicationPeformanceMetricsInOverviewData,
    getApplicationPeformanceMetricsInOverviewData,
    applicationPeformanceMetricsInOverviewLoading,
  } = useOverview();

  const { nrmAppPerfScoreCardData, getNRMAppPerformanceScoreCardData } =
    useApplicationPerformance();
  useEffect(() => {
    const applicationMetricCriteria = {
      ...operationalMetricCriteria,
      categories: categories,
      platforms: selectedApplicationNames,
      ...(selectedProgram?.programId === constants.programs.Manufacturing && {
        siteId: selectedPlant?.id ? selectedPlant?.id : null,
      }),
    };
    const incidentMetricCriteria = {
      ...operationalMetricCriteria,
      categories: categories,
      applicationNames:
        selectedProduct?.productId === constants.products.PepsiConnect
          ? constants?.applicationPerformance?.assignedGroupNames
          : [],
      ...((selectedProduct?.productId === constants.products.Kazandirio ||
        selectedProduct?.productId === constants.products.MesLite ||
        selectedProduct?.productId === constants.products.DTC ||
        selectedProduct?.productId ===
          constants.products.PepsiTastyRewards) && {
        assignmentGroupId: defaultAssignmentGroup?.value,
        period: defaultFrequency?.value,
      }),
      ...(selectedProgram?.programId === constants.programs.Manufacturing && {
        siteId: selectedPlant?.id ? selectedPlant?.id : null,
      }),
    };

    // For MesLite, only proceed if selectedPlant is available
    if (
      selectedProduct?.productId === constants.products.MesLite &&
      !selectedPlant?.id
    ) {
      return;
    }

    getApplicationPeformanceMetricsInOverviewData({
      variables: {
        applicationMetricCriteria,
        incidentMetricCriteria,
      },
    });
  }, [
    getApplicationPeformanceMetricsInOverviewData,
    categories,
    selectedApplicationNames,
    ytdCheckbox,
    selectedProduct,
    selectedPlant,
    selectedProgram,
    operationalMetricCriteria,
  ]);

  useEffect(() => {
    const scoreCardParams = {
      ...operationalMetricCriteria,
    };
    if (selectedProduct?.productId == constants?.products?.Kazandirio) {
      getNRMAppPerformanceScoreCardData({
        variables: {
          applicationMetricCriteria: scoreCardParams,
        },
      });
    }
  }, [getNRMAppPerformanceScoreCardData, operationalMetricCriteria]);

  const getMetricsData = () => {
    if (
      selectedProduct?.productId == constants?.products?.Kazandirio ||
      selectedProduct?.productId == constants?.products?.MesLite ||
      selectedProduct?.productId == constants?.products?.DTC ||
      selectedProduct?.productId == constants?.products?.PepsiTastyRewards
    ) {
      const apiData = {
        applicationPeformanceMetricsInOverviewData,
        nrmAppPerfScoreCardData,
        selectedProduct,
      };
      return incidentMonitoringScorecrad(apiData, ytdCheckbox)?.metrics;
    } else {
      return createAppPerformanceData(
        applicationPeformanceMetricsInOverviewData,
        ytdCheckbox
      )?.metrics;
    }
  };
  const metrics = getMetricsData();

  // Get product-specific metrics configuration
  const getProductMetricsConfig = (metrics: any[], productId?: number) => {
    // Define configurations for different products
    switch (productId) {
      case constants.products.Kazandirio:
      case constants.products.DTC:
      case constants.products.PepsiTastyRewards:
        return [
          { metrics: [metrics[0], metrics[1]], columns: 2 },
          { metrics: [metrics[2], metrics[3], metrics[4]], columns: undefined },
        ];
      case constants.products.MesLite:
        return [
          { metrics: [metrics[2], metrics[3], metrics[4]], columns: undefined },
        ];
      default:
        return [{ metrics: metrics, columns: undefined }];
    }
  };

  // Render metrics view based on product configuration
  const getMetricsView = (metrics: any[]) => {
    const metricsConfig = getProductMetricsConfig(
      metrics,
      selectedProduct?.productId
    );

    return (
      <>
        {metricsConfig.map((config, index) => (
          <IncidentCountView
            key={`metrics-group-${index}`}
            metrics={config.metrics}
            columns={config.columns}
          />
        ))}
      </>
    );
  };

  return (
    <CardContainer
      slug={slug}
      title={title}
      showInfoIcon={true}
      dataDefinitions={{
        sections:
          KpiDefinitionData[selectedProduct?.productId]?.overview
            ?.applicationPerformance ?? [],
      }}
      isLoading={applicationPeformanceMetricsInOverviewLoading}
      isDraggable={isOverview ? false : true}
    >
      <GridContainer>
        <KpiContainer title={isOverview ? '' : 'Incident Monitoring'}>
          {getMetricsView(metrics)}
        </KpiContainer>
        <KpiContainer title="Incident Trend">
          <Styled.CharContainerHeight>
            <MixLineOverlapBarChart
              data={createDataList(
                applicationPeformanceMetricsInOverviewData,
                isMobile,
                isTablet
              )}
              height={'230%'}
            />
          </Styled.CharContainerHeight>
        </KpiContainer>
      </GridContainer>
    </CardContainer>
  );
};

export default AppPerformanceHOC;
